import type { GetSessionSnapshotResponse } from "@nlc/shared-types";

import type { InFlightUpload, SessionFileRow } from "../types/sessionFile";

function getLiveAttachmentIds(
  snapshot: GetSessionSnapshotResponse,
): Set<number> {
  return new Set(snapshot.attachments.map((entry) => entry.attachment.id));
}

/**
 * Drops stale in-flight rows once the snapshot reflects the same attachment,
 * and removes orphaned entries whose attachment was deleted server-side.
 */
export function reconcileInFlightUploads(
  inFlightUploads: InFlightUpload[],
  snapshot: GetSessionSnapshotResponse | undefined,
): InFlightUpload[] {
  if (!snapshot) return inFlightUploads;

  const liveAttachmentIds = getLiveAttachmentIds(snapshot);

  return inFlightUploads.filter((upload) => {
    if (upload.replacesAttachmentId !== undefined) {
      return upload.status === "error" || isActiveInFlight(upload);
    }

    if (upload.attachmentId === undefined) {
      return true;
    }

    if (
      upload.status === "complete" &&
      liveAttachmentIds.has(upload.attachmentId)
    ) {
      return false;
    }

    if (liveAttachmentIds.has(upload.attachmentId)) {
      return isActiveInFlight(upload);
    }

    return upload.status === "error" || isActiveInFlight(upload);
  });
}

function isActiveInFlight(upload: InFlightUpload): boolean {
  return (
    upload.status === "uploading" ||
    upload.status === "attaching" ||
    upload.status === "error"
  );
}

/** File is linked to the session and not mid-upload. */
export function isSessionFileAttached(
  row: Pick<SessionFileRow, "attachmentId" | "status">,
): boolean {
  return (
    row.attachmentId !== null &&
    row.status !== "uploading" &&
    row.status !== "attaching" &&
    row.status !== "error"
  );
}

/** Multipart upload or session attach still running. */
export function isSessionFileTransferring(
  row: Pick<SessionFileRow, "status">,
): boolean {
  return (
    row.status === "uploading" ||
    row.status === "attaching" ||
    row.status === "replacing"
  );
}
